import React from 'react'
import { useNavigate } from 'react-router-dom'

import InboxPromo from '../../../assets/images/promo-inbox.png'

import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { Button } from '../../../components/Button/Button'
import { Modal } from '../../../components/Modal/Modal'

import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'

import { useGetBillingCustomerQuery, useGetPlansQuery } from '../../../store/plan/plan'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { dateDiffInDays } from '../../../common/util'

import './ContactEmailResponseModal.scss'

interface ContactEmailResponseModalProps {
  show: boolean
  onCancel: () => void
}

export const ContactEmailResponseModal: React.FC<ContactEmailResponseModalProps> = ({ show, onCancel }) => {
  const { isFetching: quotasFetching, hasQuota, quotaSuccess } = useZocksQuota()

  if (!quotaSuccess && quotasFetching) {
    return null
  }

  const emailEnabled = hasQuota('integration.emailEnabled.value')

  return (
    <Modal
      show={show}
      size={emailEnabled ? 'lg' : 'xl'}
      onHide={onCancel}
      header={<></>}
      body={<ContactEmailResponseModalBody emailEnabled={emailEnabled} />}
    />
  )
}

interface ContactEmailResponseModalBodyProps {
  emailEnabled: boolean
}

const ContactEmailResponseModalBody: React.FC<ContactEmailResponseModalBodyProps> = ({ emailEnabled }) => {
  const navigate = useNavigate()

  if (!emailEnabled) {
    return (
      <div className='d-flex flex-column align-items-center zocks-gap-2 zocks-pb-2.5'>
        <div className='d-flex flex-column align-items-center gap-3 zocks-px-2.0 contact-email-response-modal-header'>
          <div className='d-flex align-items-center gap-2'>
            <i className='bi bi-lock' />
            <h4>There's a better way to handle emails with Zocks</h4>
          </div>
          <p className='m-0 paragraph-medium text-center'>
            <span>
              Email reply has evolved into Inbox on our Ultimate plan. Inbox automatically generates AI-powered email replies using your full client context.
            </span>{' '}
            <span className='bold'>No manual copy-pasting required.</span>
          </p>
        </div>
        <Upsell feature={DELTA_TRIAL_FEATURES['inbox']} />
      </div>
    )
  }

  const handleGoToInbox = () => {
    navigate('/email/inbox')
  }

  return (
    <div className='d-flex align-items-center zocks-gap-2 zocks-pb-2.5'>
      <div className='zocks-bg-color-primary-light px-4 rounded-3 align-self-stretch d-flex align-items-center'>
        <a href='https://youtu.be/0vF8GGSg3Bk' target='_blank' rel='noopener noreferrer'>
          <img src={InboxPromo} alt='Watch inbox demo' className='zocks-border-0.5 contact-email-response-modal-upsell' />
        </a>
      </div>
      <div className='d-flex flex-column gap-4 py-4'>
        <FreeTrialInfo />
        <div className='d-flex flex-column zocks-gap-1.25'>
          <h4>Email reply has moved to Inbox</h4>
          <p className='m-0 paragraph-medium'>
            <span>Inbox includes everything from Email reply, plus automatic categorization and AI-powered replies using your full client context.</span>{' '}
            <span className='bold'>No manual copy-pasting required.</span>
          </p>
        </div>
        <Button variant='primary' className='px-3 py-2 button-medium align-self-start' onClick={handleGoToInbox}>
          Go to Inbox now
        </Button>
      </div>
    </div>
  )
}

const FreeTrialInfo = () => {
  const accountId = useAppSelector(selectAccountId)

  const { planId } = DELTA_TRIAL_FEATURES['inbox']

  const { data: plans } = useGetPlansQuery()
  const { data: customer } = useGetBillingCustomerQuery({ accountId })

  const plan = plans?.data.find((plan) => plan.id === planId) ?? null

  const freeTrialDays =
    customer && customer.data.freeTrial && !customer.data.freeTrial.endedAt && customer.data.subscription
      ? dateDiffInDays(new Date(customer.data.subscription.currentPeriodStart), new Date(customer.data.subscription.currentPeriodEnd))
      : 0

  const usedFreeTrialDays =
    customer && customer.data.freeTrial && customer.data.subscription && !customer.data.freeTrial.endedAt
      ? dateDiffInDays(new Date(customer.data.subscription.currentPeriodStart), new Date())
      : 0

  const remainingFreeTrialDays = freeTrialDays - usedFreeTrialDays

  if (!plan || !customer?.data.freeTrial || customer.data.freeTrial.endedAt) {
    return null
  }

  return (
    <div className='d-flex align-items-center gap-2'>
      <div className='px-2 py-1 zocks-bg-color-primary-light rounded-1 lead-small'>{plan.name} Plan</div>
      <span className='paragraph-small zocks-text-gray-700'>
        {remainingFreeTrialDays} {remainingFreeTrialDays === 1 ? 'day' : 'days'} left in your trial
      </span>
    </div>
  )
}
